import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { api, BACKEND } from "@/lib/api";

const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));
const GENERIC = "Your browser can't play this file and no conversion is available.";

export function usePlayback(videoId, startAt) {
  const ref = useRef(null);
  const hls = useRef(null);
  const session = useRef(null);
  const wanted = useRef(0);
  const srcUrl = useRef("");
  const extra = useRef({});
  const fallbackRef = useRef(() => {});
  const [base, setBase] = useState(null);
  const [cur, setCur] = useState(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");

  const stopPlex = useCallback(() => {
    if (session.current) api.post(`/plex/hls/${videoId}/stop`, null, { params: { session: session.current } }).catch(() => {});
    session.current = null;
  }, [videoId]);

  const load = useCallback((src, at) => {
    const el = ref.current;
    if (!el || !src) return;
    hls.current?.destroy();
    hls.current = null;
    setError("");
    setCur(src);
    wanted.current = at;
    const go = () => el.play().catch(() => {});
    if (src.mode === "remux") {
      setOffset(at);
      const q = new URLSearchParams({ start: Math.floor(at) });
      if (extra.current.audio) q.set("audio", extra.current.audio);
      if (extra.current.burn != null) q.set("burn", extra.current.burn);
      el.src = srcUrl.current = `${BACKEND}${src.url}?${q}`;
      return go();
    }
    setOffset(0);
    const seekThenPlay = () => { if (at > 0) el.currentTime = at; go(); };
    if (src.mode === "hls") {
      stopPlex();
      session.current = newSession();
      const url = `${BACKEND}${src.url}?session=${session.current}`;
      if (Hls.isSupported()) {
        const h = new Hls({ xhrSetup: (x) => { x.withCredentials = true; }, maxBufferLength: 60 });
        h.on(Hls.Events.MANIFEST_PARSED, seekThenPlay);
        h.on(Hls.Events.ERROR, (_, d) => { if (d.fatal) console.warn("hls fatal", d.type, d.details, d.reason || d.error?.message || ""); if (d.fatal) setError("Plex could not transcode this video. Check that your server is reachable."); });
        h.loadSource(url);
        h.attachMedia(el);
        hls.current = h;
      } else {
        el.src = url;
        el.addEventListener("loadedmetadata", seekThenPlay, { once: true });
      }
      return;
    }
    el.src = srcUrl.current = `${BACKEND}${src.url}`;
    // Raw MKV/MP4 with an undecodable video codec loads with no picture: switch to conversion.
    el.addEventListener("loadedmetadata", () => { if (el.videoWidth === 0) { console.warn("direct: no video track decoded"); fallbackRef.current(); } else seekThenPlay(); }, { once: true });
  }, [stopPlex]);

  useEffect(() => {
    let alive = true;
    api.get(`/videos/${videoId}/play`).then(({ data }) => { if (alive) { setBase(data); load(data, startAt); } })
      .catch(() => setError("This video is unavailable right now."));
    return () => { alive = false; hls.current?.destroy(); stopPlex(); };
  }, [videoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const now = useCallback(() => offset + (ref.current?.currentTime || 0), [offset]);

  const fallback = useCallback(() => {
    const el = ref.current;
    if (el?.error) console.warn("media error", el.error.code, el.error.message);
    const pos = el?.currentTime > 0 ? offset + el.currentTime : wanted.current;
    if (cur?.mode === "direct" && base?.convert) return load(base.convert, pos);
    fetch(srcUrl.current, { credentials: "include", headers: { Range: "bytes=0-1" } })
      .then((r) => (r.ok ? null : r.json())).then((j) => setError(j?.detail || GENERIC)).catch(() => setError(GENERIC));
  }, [base, cur, load, offset]);
  fallbackRef.current = fallback;

  return { ref, base, cur, offset, error, load, fallback, extra, now };
}
